import { type ReactNode, type FC } from 'react';
import { Space, Text } from '@g2u/ui-kit';
import cx from 'classnames';

import { type WithOptionalDataTest } from 'types';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import styles from './details.module.scss';

type Props = {
  title?: string;
  columns?: boolean;
  table?: boolean;
  size?: Size;
  children?: ReactNode;
} & WithOptionalDataTest;

export enum Size {
  Short = 'short',
  Wide = 'wide',
  Full = 'full',
}

const sizeClassNames = {
  [Size.Short]: '',
  [Size.Wide]: styles.details_wide,
  [Size.Full]: styles.details_full,
};

const Item: FC<{
  label?: ReactNode;
  value?: ReactNode;
  tooltip?: string;
  hasNone?: boolean;
}> = (props) => {
  const { label, value, tooltip, hasNone } = props;
  const formattedValue = !value && hasNone ? 'None' : value;
  return (
    <>
      <Text.$>
        {label}{' '}
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoCircleOutlined className={styles.details__icon} />
          </Tooltip>
        )}
      </Text.$>
      <Text.$>{formattedValue}</Text.$>
    </>
  );
};

const Row: FC<{
  label?: ReactNode;
  value?: ReactNode;
  tooltip?: string;
}> = (props) => {
  const { label, value, tooltip } = props;
  return (
    <>
      <Text.$>
        {label}{' '}
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoCircleOutlined className={styles.details__icon} />
          </Tooltip>
        )}
      </Text.$>
      <Text.$>{value}</Text.$>
    </>
  );
};

export const Details = (props: Props) => {
  const { columns, title, size = Size.Short, table, children } = props;
  return (
    <Space.$
      direction={Space.Direction.Vertical}
      size={Space.Size.Middle}
      className={cx(styles.details, sizeClassNames[size])}
    >
      {title && <Text.$ className={styles.details__title}>{title}</Text.$>}
      {columns && <div className={styles.details__items}>{children}</div>}
      {table && <div className={styles.details__rows}>{children}</div>}
      {!columns && !table && children}
    </Space.$>
  );
};

Details.Item = Item;
Details.Row = Row;
Details.Size = Size;
